import { CardCoreVM, normalizeDbCard } from "@/lib/cardCoreNormalizer";
import { HeroCardData } from "@/components/HeroSplitScreen";
import { Database } from "@/integrations/supabase/types";
import { getCardCategory } from "@/lib/cardCategory";

type DbCard = Database['public']['Tables']['cards']['Row'];

/**
 * Converts normalized CardCoreVM to HeroCardData format
 * Uses clean, normalized data instead of raw database values
 * Note: logoUrl must be passed separately as it's not in CardCoreVM
 */
export function toHeroCardVM(core: CardCoreVM, logoUrl?: string | null, rawCard?: any): HeroCardData {
  // Extract numeric values from formatted strings
  const effectiveRate = core.effectiveRate 
    ? parseFloat(core.effectiveRate.replace(',', '.').replace('%', '').trim())
    : 0;
  
  const annualFee = core.annualFee === "Gratis" 
    ? 0 
    : parseFloat(core.annualFee?.replace(/[^\d]/g, '') || '0');
  
  // Extract max credit limit from range string like "Opptil 150 000 kr"
  const creditLimitMatch = core.creditLimit?.match(/[\d\s]+(?=\s*kr)/);
  const creditLimit = creditLimitMatch 
    ? parseFloat(creditLimitMatch[0].replace(/\s/g, ''))
    : 0;
  
  // Extract raw credit limit range from database
  const creditLimitMin = rawCard?.credit_limit_min || null;
  const creditLimitMax = rawCard?.credit_limit_max || null;
  const category = getCardCategory(rawCard);
  const cardType = category === "revolving" ? "credit" : category;
  const aprNominal = category === "revolving" ? (rawCard?.apr_nominal || null) : null;
  
  // Extract interest-free days from string like "45 d"
  const interestFreePeriod = core.interestFree 
    ? parseInt(core.interestFree.replace(/\D/g, ''))
    : 0;
  
  // Build concise subtitle from normalized signals (max 110 chars)
  const hasTravel = core.travelInsurance || core.tags.some(t => /reiseversicherung/i.test(t.label));
  const hasRewards = core.bullets.some(b => /cashback|bonus|poeng/i.test(b)) || 
                     core.tags.some(t => /cashback|bonus/i.test(t.label));
  
  const pitchParts: string[] = [];
  if (hasTravel && hasRewards) {
    pitchParts.push('Reiseversicherung und Cashback/Bonus');
  } else if (hasTravel) {
    pitchParts.push('Inkludierte Reiseversicherung');
  } else if (hasRewards) {
    pitchParts.push('Cashback/Bonus auf Einkäufe');
  }
  
  if (core.bullets[0]) {
    pitchParts.push(core.bullets[0]);
  }
  
  const rawSubtitle = pitchParts.join(' · ');
  const subtitle = rawSubtitle.length > 110 
    ? rawSubtitle.substring(0, 107) + '...' 
    : rawSubtitle;
  
  return {
    name: core.name,
    bank: core.issuer,
    imageUrl: logoUrl || null,
    subtitle,
    effectiveRate: category === "revolving" ? effectiveRate : 0,
    annualFee,
    creditLimit,
    creditLimitMin,
    creditLimitMax,
    cardType,
    aprNominal,
    interestFreePeriod,
    network: rawCard?.network || null,
    currentAccountIncluded: rawCard?.current_account_included || false,
    isPrepaid: category === "prepaid",
    
    // Feature flags (extracted from core data)
    travelInsurance: core.travelInsurance || false,
    feesForPurchasesAbroad: 0,
    hasCashback: core.bullets.some(b => 
      /cashback|bonus|poeng/i.test(b)
    ) || core.tags.some(t => /cashback|bonus/i.test(t.label)),
    hasLounge: core.bullets.some(b => 
      /lounge/i.test(b)
    ) || core.tags.some(t => /lounge/i.test(t.label)),
    hasCarRentalInsurance: core.bullets.some(b => 
      /leiebil|car rental/i.test(b)
    ),
    hasDiscounts: core.bullets.some(b => 
      /rabatt|discount/i.test(b)
    ),
    isMembershipRequired: core.isMembershipOnly || false,
  };
}

/**
 * Helper for cases where you have raw DbCard
 * Normalizes first, then converts to hero format
 */
export function adaptDbCardToHeroCard(card: DbCard): HeroCardData {
  const core = normalizeDbCard(card);
  return toHeroCardVM(core, card.logo_url);
}

